import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../../components/ui/Layout';
import { useAuth } from '../../components/context/AuthContext';
import { db } from '../../firebase';
import { doc, onSnapshot, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import { Container, Card, ListGroup, Spinner, Alert, Button, Row, Col, Badge, Image } from 'react-bootstrap';
import { createLogger } from '../../utils/logger';

const logger = createLogger('UserOrderStatusPage');

const UserOrderStatusPage = () => {
  const { transactionId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productImages, setProductImages] = useState({}); // { [productId]: imageUrl }
  const [productDetails, setProductDetails] = useState({}); // { [productId]: productData }
  const [employeeName, setEmployeeName] = useState(null); // Employee name who packed the order

  const breadcrumbItems = [
    { path: "/", label: "Home" },
    { path: "/order-history", label: "Order History" },
    { label: transaction ? `Order: ${transactionId.substring(0,8)}...` : "Order Status" }
  ];

  useEffect(() => {
    if (!currentUser) {
      setError("Please log in to view order status."); setLoading(false); return;
    }
    if (!transactionId) {
      setError("Transaction ID is missing."); setLoading(false); return;
    }
    const transactionRef = doc(db, 'Transactions', transactionId);
    const unsubscribe = onSnapshot(transactionRef, 
      (docSnap) => {
        if (docSnap.exists()) {
          const transData = { id: docSnap.id, ...docSnap.data() };
          // Log the transaction data for debugging
          // logger.debug("[OrderStatus] Transaction snapshot data:", transData);
          // logger.debug("currentUser:", currentUser);
          // logger.debug("transData:", transData);
          // if (
          //   transData.userId === currentUser.uid ||
          //   (currentUser.isSupermarketEmployee && transData.supermarketId === currentUser.employeeSupermarketId)
          // ) {
            setTransaction(transData); setError(null);
          // } else {
          //   setError("You are not authorized to view this order's status."); setTransaction(null);
          // }
        } else {
          logger.warn("[OrderStatus] Transaction not found for ID:", transactionId);
          setError("Order not found."); setTransaction(null);
        }
        setLoading(false);
      },
      (err) => {
        setError(`Failed to load order status. ${  err.message}`); setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [transactionId, currentUser]);

  // Function to fetch product details and images
  const fetchProductDetails = useCallback(async (productId) => {
    if (productDetails[productId]) return; // Already fetched
    
    try {
      const productDoc = await getDoc(doc(db, 'Products', productId));
      if (productDoc.exists()) {
        const productData = productDoc.data();
        setProductDetails(prev => ({ ...prev, [productId]: productData }));
        
        // Fetch product image if available
        if (productData.media && productData.media.length > 0 && productData.media[0].url) {
          try {
            const storage = getStorage();
            const imageRef = ref(storage, productData.media[0].url);
            const imageUrl = await getDownloadURL(imageRef);
            setProductImages(prev => ({ ...prev, [productId]: imageUrl }));
          } catch (imageError) {
            logger.warn(`Failed to load image for product ${productId}:`, imageError);
          }
        }
      }
    } catch (error) {
      logger.error(`Failed to fetch product details for ${productId}:`, error);
    }
  }, [productDetails]);

  // Effect to fetch product details when transaction changes
  useEffect(() => {
    if (!transaction || !transaction.cartItems) return;
    
    // Fetch details for all products in the transaction
    transaction.cartItems.forEach(item => {
      if (item.productId) {
        fetchProductDetails(item.productId);
      }
    });
  }, [transaction, fetchProductDetails]);

  // Effect to fetch employee name when packedBy is available
  useEffect(() => {
    const fetchEmployeeName = async () => {
      if (!transaction?.packedBy) {
        setEmployeeName(null);
        return;
      }

      try {
        // Try to find employee by identity_id (UID)
        const usersRef = collection(db, 'Users');
        const userQuery = query(usersRef, where('identity_id', '==', transaction.packedBy));
        const querySnapshot = await getDocs(userQuery);

        if (!querySnapshot.empty) {
          const userData = querySnapshot.docs[0].data();
          // Try different name fields that might exist (prioritize first_name as it's the standard)
          const name = (userData.first_name && userData.last_name ? `${userData.first_name} ${userData.last_name}` : null) ||
                      userData.first_name ||
                      userData.displayName || 
                      userData.display_name || 
                      userData.name ||
                      (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : null) ||
                      userData.firstName ||
                      userData.email?.split('@')[0] ||
                      'Employee';
          setEmployeeName(name);
        } else {
          // Fallback: try direct document lookup by UID
          const userDoc = await getDoc(doc(db, 'Users', transaction.packedBy));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const name = (userData.first_name && userData.last_name ? `${userData.first_name} ${userData.last_name}` : null) ||
                        userData.first_name ||
                        userData.displayName || 
                        userData.display_name || 
                        userData.name ||
                        (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : null) ||
                        userData.firstName ||
                        userData.email?.split('@')[0] ||
                        'Employee';
            setEmployeeName(name);
          } else {
            setEmployeeName(null);
          }
        }
      } catch (error) {
        logger.warn('Failed to fetch employee name:', error);
        setEmployeeName(null);
      }
    };

    fetchEmployeeName();
  }, [transaction?.packedBy]);

  const getStatusBadge = (status) => {
    let variant = 'secondary';
    let text = status ? status.replace(/_/g, ' ').replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase())) : 'Unknown';
    switch (status) {
      case 'initiated': variant = 'light'; text = 'Initiated'; break;
      case 'awaiting_payment_confirmation': variant = 'info'; text = 'Payment Processing'; break;
      case 'payment_successful': variant = 'primary'; text = 'Payment Confirmed'; break;
      case 'awaiting_packing': variant = 'warning'; text = 'Awaiting Packing'; break;
      case 'packed_awaiting_pickup': variant = 'success'; text = 'Ready for Pickup'; break;
      case 'completed': variant = 'dark'; text = 'Order Completed'; break;
      case 'cancelled_by_employee': variant = 'danger'; text = 'Cancelled by Store'; break;
      case 'payment_failed': variant = 'danger'; text = 'Payment Failed'; break;
      default: break;
    }
    return <Badge bg={variant} text={variant === 'light' || variant === 'warning' ? 'dark' : 'white'}>{text}</Badge>;
  };

  if (loading) return <Layout><Container className="mt-4 text-center"><Spinner animation="border"/> <p>Loading order status...</p></Container></Layout>;
  
  return (
    <>
      <style>{`
        /* Responsive styles for mobile devices */
        @media (max-width: 575.98px) {
          .print-section .card-header h4 {
            font-size: 1.1rem !important;
          }
          .print-section .card-header small {
            font-size: 0.75rem !important;
          }
          .print-section .text-break {
            word-break: break-word;
            overflow-wrap: break-word;
          }
          .print-section .list-group-item {
            padding: 0.75rem 0 !important;
          }
        }
        
        @media (min-width: 576px) and (max-width: 767.98px) {
          .print-section .card-header h4 {
            font-size: 1.25rem !important;
          }
        }
        
        @media print {
          body * {
            visibility: hidden;
          }
          .print-section, .print-section * {
            visibility: visible;
          }
          .print-section {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
          .print-section .card {
            border: none;
            box-shadow: none;
          }
          .print-section .card-header {
            background-color: #f38118 !important;
            color: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .print-section .btn, .print-section .badge {
            border: 1px solid #000;
          }
          .print-section .text-primary {
            color: #f38118 !important;
          }
          .print-section .text-success {
            color: #198754 !important;
          }
          .print-section .text-danger {
            color: #dc3545 !important;
          }
          .print-section .border-top {
            border-top: 2px solid #000 !important;
            margin-top: 20px;
            padding-top: 20px;
          }
          .print-section .mb-3, .print-section .mb-4 {
            margin-bottom: 15px !important;
          }
          .print-section .mt-4 {
            margin-top: 20px !important;
          }
          .print-section .shadow-sm {
            box-shadow: none !important;
          }
          .print-section .bg-light {
            background-color: #f8f9fa !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>
    <Layout>
        <Container className="mt-4 print-section">
        <div className="no-print">
        <Row className="align-items-center mb-1">
              <Col xs={12} sm="auto" className="mb-2 mb-sm-0">
                  <Button variant="outline-secondary" onClick={() => navigate(currentUser ? '/order-history' : '/carts')} className="w-100 w-sm-auto">
                      <i className="fas fa-arrow-left me-1 d-none d-sm-inline"></i>
                    &larr; Back to {currentUser ? "Order History" : "Carts"}
                </Button>
            </Col>
              <Col xs={12} sm className="d-none d-sm-block">
                <BreadcrumbNavigation items={breadcrumbItems} />
            </Col>
        </Row>
        </div>
        <h2 className="mt-3 mb-3 text-center text-sm-start">Order Status</h2>
        
        {error && <Alert variant="danger">{error}</Alert>}
        
        {transaction && (
          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white">
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
                <div className="flex-grow-1">
                  <h4 className="mb-0 fs-5 fs-sm-4">Order #{transaction.id}</h4>
                  <small className="text-white-50 d-block d-sm-inline">Order Status</small>
                </div>
                <div className="flex-shrink-0">
                  {getStatusBadge(transaction.status)}
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="mb-4">
                <h5 className="text-primary mb-3 fs-5 fs-sm-4">
                  <i className="fas fa-store me-2"></i>
                  {transaction.supermarketName}
                </h5>
              </div>

              <Row>
                <Col xs={12} md={6} className="mb-4 mb-md-0">
                  <h6 className="text-muted mb-3 border-bottom pb-2">Order Information</h6>
                  <ListGroup variant="flush" className="mb-4">
                    <ListGroup.Item className="px-0 py-2">
                      <div className="d-flex flex-column flex-sm-row justify-content-between gap-1">
                        <span className="text-muted">Date Created:</span>
                        <strong className="text-break text-sm-end">{transaction.createdAt?.toDate().toLocaleString()}</strong>
                      </div>
                    </ListGroup.Item>
                    <ListGroup.Item className="px-0 py-2">
                      <div className="d-flex flex-column flex-sm-row justify-content-between gap-1">
                        <span className="text-muted">Last Updated:</span>
                        <strong className="text-break text-sm-end">{transaction.updatedAt?.toDate().toLocaleString()}</strong>
                      </div>
                    </ListGroup.Item>
                    {transaction.cartInstanceId && (
                      <ListGroup.Item className="px-0 py-2">
                        <div className="d-flex flex-column flex-sm-row justify-content-between gap-1">
                          <span className="text-muted">Cart Reference:</span>
                          <strong className="text-break text-sm-end">{transaction.cartInstanceId.replace('cart_instance_','')}</strong>
                        </div>
                      </ListGroup.Item>
                    )}
                    {transaction.paymentGatewayTransactionId && (
                      <ListGroup.Item className="px-0 py-2">
                        <div className="d-flex flex-column flex-sm-row justify-content-between gap-1">
                          <span className="text-muted">Payment ID:</span>
                          <strong className="text-break text-sm-end" style={{maxWidth: '100%', wordBreak: 'break-all'}}>{transaction.paymentGatewayTransactionId}</strong>
                        </div>
                      </ListGroup.Item>
                    )}
                  </ListGroup>
                </Col>

                <Col xs={12} md={6}>
                  <h6 className="text-muted mb-3 border-bottom pb-2">Payment & Packing Details</h6>
                  <ListGroup variant="flush" className="mb-4">
                    {transaction.paymentType === 'full' && (
                      <ListGroup.Item className="px-0 py-2">
                        <div className="alert alert-success mb-0 p-2 text-center">
                          <strong><i className="fas fa-check-circle me-2"></i>FULL PAYMENT ORDER</strong>
                          <br />
                          <small>The full order total has been paid. Confirmation fee collected as processing fee.</small>
                        </div>
                      </ListGroup.Item>
                    )}
                    <ListGroup.Item className="px-0 py-2">
                      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start gap-1">
                        <span className="text-muted">Payment Type:</span>
                        <div className="d-flex flex-column flex-sm-row align-items-end align-items-sm-center gap-1">
                          {transaction.paymentType === 'full' ? (
                            <Badge bg="success" className="fs-6 px-3 py-2">
                              <i className="fas fa-check-circle me-1"></i>Full Payment
                            </Badge>
                          ) : (
                            <Badge bg="secondary" className="fs-6 px-3 py-2">
                              Confirmation Fee Only
                            </Badge>
                          )}
                        </div>
                      </div>
                    </ListGroup.Item>
                    <ListGroup.Item className="px-0 py-2">
                      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start gap-1">
                        <span className="text-muted">{transaction.paymentType === 'full' ? 'Processing Fee (Confirmation Fee):' : 'Confirmation Fee:'}</span>
                        <div className="d-flex flex-column flex-sm-row align-items-end align-items-sm-center gap-1">
                          <strong className="text-success">${Number(transaction.confirmationFee ?? 0).toFixed(2)}</strong>
                          {transaction.paymentType === 'full' && (
                            <small className="text-muted">(Included in total)</small>
                          )}
                          {transaction.paymentType !== 'full' && (
                            <small className="text-muted">(Paid: ${Number(transaction.amountPaid ?? 0).toFixed(2)})</small>
                          )}
                        </div>
                      </div>
                    </ListGroup.Item>
                    <ListGroup.Item className="px-0 py-2">
                      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start gap-1">
                        <span className="text-muted">Order Subtotal:</span>
                        <div className="d-flex flex-column flex-sm-row align-items-end align-items-sm-center gap-1">
                          <strong className="text-primary">${Number(transaction.orderSubtotal ?? 0).toFixed(2)}</strong>
                          {transaction.paymentType === 'full' ? (
                            <small className="text-success">(Paid)</small>
                          ) : (
                            <small className="text-muted">(Due at Pickup)</small>
                          )}
                        </div>
                      </div>
                    </ListGroup.Item>
                    {transaction.paymentType === 'full' && (
                      <ListGroup.Item className="px-0 py-2 border-top border-success border-2">
                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start gap-1">
                          <span className="text-muted fw-bold">Total Amount Paid:</span>
                          <div className="d-flex flex-column flex-sm-row align-items-end align-items-sm-center gap-1">
                            <strong className="text-success fs-5">${(Number(transaction.orderSubtotal ?? 0) + Number(transaction.confirmationFee ?? 0)).toFixed(2)}</strong>
                          </div>
                        </div>
                      </ListGroup.Item>
                    )}
                    {transaction.packedBy && (
                      <ListGroup.Item className="px-0 py-2">
                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-1">
                          <span className="text-muted">
                            <i className="fas fa-user-check me-1"></i>
                            Packed By:
                          </span>
                          <div className="text-start text-sm-end">
                            {employeeName ? (
                              <strong className="text-success d-block">{employeeName}</strong>
                            ) : (
                              <strong className="text-muted d-block">Loading...</strong>
                            )}
                            {employeeName && (
                              <small className="d-block text-muted" style={{fontSize: '0.75rem'}}>
                                Employee ID: {transaction.packedBy.substring(0, 8)}...
                              </small>
                            )}
                          </div>
                        </div>
                      </ListGroup.Item>
                    )}
                    {transaction.packedAt && (
                      <ListGroup.Item className="px-0 py-2">
                        <div className="d-flex flex-column flex-sm-row justify-content-between gap-1">
                          <span className="text-muted">
                            <i className="fas fa-clock me-1"></i>
                            Packed At:
                          </span>
                          <strong className="text-break text-sm-end">{transaction.packedAt?.toDate().toLocaleString()}</strong>
                        </div>
                      </ListGroup.Item>
                    )}
                    {transaction.pickedUpAt && (
                      <ListGroup.Item className="px-0 py-2">
                        <div className="d-flex flex-column flex-sm-row justify-content-between gap-1">
                          <span className="text-muted">
                            <i className="fas fa-hand-holding me-1"></i>
                            Picked Up At:
                          </span>
                          <strong className="text-break text-sm-end">{transaction.pickedUpAt?.toDate().toLocaleString()}</strong>
                        </div>
                      </ListGroup.Item>
                    )}
                    {transaction.completedAt && (
                      <ListGroup.Item className="px-0 py-2">
                        <div className="d-flex flex-column flex-sm-row justify-content-between gap-1">
                          <span className="text-muted">
                            <i className="fas fa-check-circle me-1"></i>
                            Completed At:
                          </span>
                          <strong className="text-break text-sm-end">{transaction.completedAt?.toDate().toLocaleString()}</strong>
                        </div>
                      </ListGroup.Item>
                    )}
                    {transaction.cancelledAt && (
                      <ListGroup.Item className="px-0 py-2">
                        <div className="d-flex flex-column flex-sm-row justify-content-between gap-1">
                          <span className="text-muted">
                            <i className="fas fa-times-circle me-1"></i>
                            Cancelled At:
                          </span>
                          <strong className="text-danger text-break text-sm-end">{transaction.cancelledAt?.toDate().toLocaleString()}</strong>
                        </div>
                      </ListGroup.Item>
                    )}
                    {transaction.cancellationReason && (
                      <ListGroup.Item className="px-0 py-2">
                        <div>
                          <span className="text-muted d-block mb-1">Cancellation Reason:</span>
                          <strong className="text-danger">{transaction.cancellationReason}</strong>
                        </div>
                      </ListGroup.Item>
                    )}
              </ListGroup>
                </Col>
              </Row>

              <div className="mt-4 pt-3 border-top">
                <h5 className="mb-3 fs-5">
                  <i className="fas fa-shopping-bag me-2"></i>
                  Items in this Order
                </h5>
              {transaction.cartItems && transaction.cartItems.length > 0 ? (
                <ListGroup variant="flush">
                  {transaction.cartItems.map((item, index) => {
                    const productDetail = productDetails[item.productId];
                    const productImage = productImages[item.productId];
                    const packedItemDetail = transaction.packedItemsDetails?.find(pi => pi.productId === item.productId);
                    
                    return (
                      <ListGroup.Item key={item.productId || index} className="py-3">
                        <div className="d-flex flex-column flex-sm-row align-items-start gap-3">
                          {/* Product Image */}
                          <div className="flex-shrink-0 mx-auto mx-sm-0" style={{ width: '80px', height: '80px' }}>
                            {productImage ? (
                              <Image 
                                src={productImage} 
                                alt={item.name || 'Product'} 
                                className="img-fluid rounded"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <div 
                                className="d-flex align-items-center justify-content-center bg-light rounded"
                                style={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  fontSize: '0.8rem',
                                  color: '#6c757d'
                                }}
                              >
                                No Image
                              </div>
                            )}
                          </div>
                          
                          {/* Product Details */}
                          <div className="flex-grow-1 w-100">
                            <h6 className="mb-1 fs-6">
                              {item.name || productDetail?.display_name || 'Product'} <span className="text-muted">(Qty: {item.quantity})</span>
                            </h6>
                            <p className="mb-1 text-muted small">
                              <span className="d-block d-sm-inline">Price: ${Number(item.price ?? 0).toFixed(2)} each</span>
                              <span className="d-block d-sm-inline ms-sm-2">- Total: <strong>${(Number(item.price ?? 0) * Number(item.quantity ?? 0)).toFixed(2)}</strong></span>
                            </p>
                            {productDetail?.description && (
                              <p className="mb-2 small text-muted">
                                {productDetail.description.length > 100 
                                  ? `${productDetail.description.substring(0, 100)}...` 
                                  : productDetail.description}
                              </p>
                            )}
                            
                            {/* Packed Photo (if available) */}
                            {packedItemDetail?.photoUrl && (
                              <div className="mt-2">
                                <Image 
                                  src={packedItemDetail.photoUrl} 
                                  alt={`Packed ${item.name}`} 
                                  className="img-thumbnail"
                                  style={{maxWidth: '100px', maxHeight: '100px'}} 
                                />
                                <small className="d-block text-muted mt-1">
                                  Packed Photo: {packedItemDetail.photoName || 'Photo'}
                                </small>
                              </div>
                            )}
                          </div>
                        </div>
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              ) : <p>No item details available.</p>}
              </div>
            </Card.Body>
            <Card.Footer className="bg-light no-print">
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-2">
                <div className="d-flex flex-column flex-sm-row gap-2 w-100 w-sm-auto">
                  <Button 
                    variant="outline-info" 
                    className="flex-fill flex-sm-grow-0"
                    onClick={() => window.print()}
                  >
                    <i className="fas fa-print me-1"></i>
                    <span className="d-none d-sm-inline">Print Order Details</span>
                    <span className="d-sm-none">Print</span>
                  </Button>
                  <Link to="/carts" className="btn btn-outline-primary flex-fill flex-sm-grow-0 text-center">
                    <i className="fas fa-shopping-cart me-1"></i>
                    <span className="d-none d-sm-inline">Go to Carts</span>
                    <span className="d-sm-none">Carts</span>
                  </Link>
                  <Link to="/" className="btn btn-outline-secondary flex-fill flex-sm-grow-0 text-center">
                    <i className="fas fa-home me-1"></i>
                    <span className="d-none d-sm-inline">Continue Shopping</span>
                    <span className="d-sm-none">Shop</span>
                  </Link>
                </div>
                {transaction.status === 'packed_awaiting_pickup' && (
                  <Badge bg="success" className="px-3 py-2 align-self-center align-self-sm-auto">
                    <i className="fas fa-check-circle me-1"></i>
                    <span className="d-none d-sm-inline">Ready for Pickup!</span>
                    <span className="d-sm-none">Ready!</span>
                  </Badge>
                )}
              </div>
            </Card.Footer>
          </Card>
        )}
        {!transaction && !loading && !error && <Alert variant="info">Order details could not be loaded.</Alert>}
      </Container>
    </Layout>
    </>
  );
};

export default UserOrderStatusPage;
